import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../firebaseClient';
import { useAuth } from 'contexts/AuthContext';
import Icon from 'components/AppIcon';

const MessageReactions = ({ messageId, reactions = {}, onReactionUpdate, onEmojiSelect, showEmojiPicker = false }) => {
  const { user } = useAuth();
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // WhatsApp-grade emoji collection with categories
  const emojiCategories = {
    'Smileys & People': [
      '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇',
      '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪', '😝', '🤑',
      '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬',
      '🤥', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶',
      '🥴', '😵', '🤯', '🤠', '🥳', '😎', '🤓', '🧐'
    ],
    'Animals & Nature': [
      '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷',
      '🐸', '🐵', '🙈', '🙉', '🙊', '🐒', '🐔', '🐧', '🐦', '🐤', '🐣', '🐥', '🦆',
      '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜',
      '🦟', '🦗', '🕷', '🕸', '🦂', '🐢', '🐍', '🦎', '🦖', '🦕', '🐙', '🦑', '🦐',
      '🦞', '🦀', '🐡', '🐠', '🐟', '🐬', '🐳', '🐋', '🦈', '🐊', '🐅', '🐆', '🦓',
      '🦍', '🦧', '🐘', '🦛', '🦏', '🐪', '🐫', '🦒', '🦘', '🐃', '🐂', '🐄', '🐎',
      '🐖', '🐏', '🐑', '🦙', '🐐', '🦌', '🐕', '🐩', '🦮', '🐕‍🦺', '🐈', '🐓', '🦃',
      '🦚', '🦜', '🦢', '🦩', '🕊', '🐇', '🦝', '🦨', '🦡', '🦦'
    ],
    'Food & Drink': [
      '🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈', '🍒', '🍑', '🥭',
      '🍍', '🥥', '🥝', '🍅', '🍆', '🥑', '🥦', '🥬', '🥒', '🌶', '🫑', '🌽', '🥕',
      '🫒', '🧄', '🧅', '🥔', '🍠', '🥐', '🥯', '🍞', '🥖', '🥨', '🧀', '🥚', '🍳',
      '🧈', '🥞', '🧇', '🥓', '🥩', '🍗', '🍖', '🦴', '🌭', '🍔', '🍟', '🍕', '🫓',
      '🥪', '🥙', '🧆', '🌮', '🌯', '🫔', '🥗', '🥘', '🫕', '🥫', '🍝', '🍜', '🍲',
      '🍛', '🍣', '🍱', '🥟', '🦪', '🍤', '🍙', '🍚', '🍘', '🍥', '🥠', '🥮', '🍢',
      '🍡', '🍧', '🍨', '🍦', '🥧', '🧁', '🍰', '🎂', '🍮', '🍭', '🍬', '🍫', '🍿',
      '🍩', '🍪', '🌰', '🥜', '🍯'
    ],
    'Activities': [
      '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱', '🪀', '🏓', '🏸',
      '🏒', '🏑', '🥍', '🏏', '🪃', '🥅', '⛳', '🪁', '🏹', '🎣', '🤿', '🥊', '🥋',
      '🎽', '🛹', '🛷', '⛸', '🥌', '🎿', '⛷', '🏂', '🪂', '🏋️‍♀️', '🏋️', '🤼‍♀️',
      '🤼', '🤸‍♀️', '🤸', '⛹️‍♀️', '⛹️', '🤺', '🤾‍♀️', '🤾', '🏌️‍♀️', '🏌️',
      '🏇', '🧘‍♀️', '🧘', '🏄‍♀️', '🏄', '🏊‍♀️', '🏊', '🤽‍♀️', '🤽', '🚣‍♀️',
      '🚣', '🧗‍♀️', '🧗', '🚵‍♀️', '🚵', '🚴‍♀️', '🚴', '🏆', '🥇', '🥈', '🥉',
      '🏅', '🎖', '🏵', '🎗', '🎫', '🎟', '🎪', '🤹', '🤹‍♀️', '🎭', '🩰', '🎨',
      '🎬', '🎤', '🎧', '🎼', '🎵', '🎶', '🥁', '🪘', '🎷', '🎺', '🎸', '🪕', '🎻'
    ],
    'Objects': [
      '📱', '📲', '☎️', '📞', '📟', '📠', '🔋', '🔌', '💻', '🖥', '🖨', '⌨️', '🖱',
      '🖲', '💽', '💾', '💿', '📀', '🧮', '🎥', '🎞', '📹', '📷', '📸', '📺', '📻',
      '🎙', '🎚', '🎛', '🧭', '⏱', '⏲', '⏰', '🕰', '⌛', '⏳', '📡', '🔭', '🔬',
      '🕳', '🩹', '🩺', '💊', '💉', '🩸', '🧬', '🦠', '🧫', '🧪', '🌡', '🧹', '🧺',
      '🧻', '🚽', '🚰', '🚿', '🛁', '🛀', '🧴', '🧷', '🧸', '🧵', '🧶', '🪡', '🧾',
      '🗞', '📃', '📄', '📋', '📊', '📈', '📉', '📇', '🗃', '🗳', '🗄', '📂', '📁',
      '🗂', '🗞', '📰', '📓', '📔', '📒', '📕', '📗', '📘', '📙', '📚', '📖', '🔖',
      '🧷', '🔗', '📎', '🖇', '📐', '📏', '🧮', '📌', '📍', '✂️', '🖊', '🖋', '✒️',
      '🖌', '🖍', '📝', '✏️', '🔍', '🔎', '🔏', '🔐', '🔒', '🔓'
    ],
    'Symbols': [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞',
      '💓', '💗', '💖', '💘', '💝', '💟', '☮️', '✝️', '☪️', '🕉', '☸️', '✡️', '🔯',
      '🕎', '☯️', '☦️', '🛐', '⛎', '♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐',
      '♑', '♒', '♓', '🆔', '⚛️', '🉑', '☢️', '☣️', '📴', '📳', '🈶', '🈚', '🈸',
      '🈺', '🈷️', '✴️', '🆚', '💮', '🉐', '㊙️', '㊗️', '🈴', '🈵', '🈹', '🈲',
      '🅰️', '🅱️', '🆎', '🆑', '🅾️', '🆘', '❌', '⭕', '🛑', '⛔', '📛', '🚫',
      '💯', '💢', '♨️', '🚷', '🚯', '🚳', '🚱', '🔞', '📵', '🚭', '❗', '❕', '❓',
      '❔', '‼️', '⁉️', '🔅', '🔆', '〽️', '⚠️', '🚸', '🔱', '⚜️', '🔰', '♻️',
      '✅', '🈯', '💹', '❇️', '✳️', '❎', '🌐', '💠', 'Ⓜ️', '🌀', '💤', '🏧',
      '🚾', '♿', '🅿️', '🈳', '🈂️', '🛂', '🛃', '🛄', '🛅', '🚹', '🚺', '🚼'
    ]
  };

  const allEmojis = Object.values(emojiCategories).flat();

  const filteredEmojis = searchTerm 
    ? allEmojis.filter(emoji => {
        // Simple search by emoji or common names
        const emojiNames = {
          '😀': 'happy smile grin',
          '😍': 'love heart eyes',
          '😂': 'laugh crying joy',
          '👍': 'thumbs up like good',
          '❤️': 'heart love red',
          '🔥': 'fire hot flame',
          '💯': 'hundred perfect score',
          '🎉': 'party celebration confetti',
          '👏': 'clap applause',
          '🙏': 'pray thanks please'
        };
        const name = emojiNames[emoji] || '';
        return name.toLowerCase().includes(searchTerm.toLowerCase());
      })
    : allEmojis;

  const handleReaction = async (reactionName) => {
    if (isUpdating) return;

    try {
      setIsUpdating(true);
      const messageRef = doc(db, 'comments', messageId);
      const userReactions = reactions[reactionName] || [];
      const hasReacted = userReactions.includes(user.uid);

      if (hasReacted) {
        // Remove reaction
        await updateDoc(messageRef, {
          [`reactions.${reactionName}`]: arrayRemove(user.uid)
        });
      } else {
        // Add reaction
        await updateDoc(messageRef, {
          [`reactions.${reactionName}`]: arrayUnion(user.uid)
        });
      }

      // Update local state
      const newReactions = { ...reactions };
      if (hasReacted) {
        newReactions[reactionName] = userReactions.filter(uid => uid !== user.uid);
        if (newReactions[reactionName].length === 0) {
          delete newReactions[reactionName];
        }
      } else {
        newReactions[reactionName] = [...userReactions, user.uid];
      }

      onReactionUpdate && onReactionUpdate(newReactions);
      setShowReactionPicker(false);
    } catch (error) {
      console.error('Error updating reaction:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const getReactionCount = (reactionName) => {
    return reactions[reactionName]?.length || 0;
  };

  const hasUserReacted = (reactionName) => {
    return reactions[reactionName]?.includes(user.uid) || false;
  };

  const getTopReactions = () => {
    return Object.entries(reactions)
      .filter(([name, users]) => users.length > 0)
      .sort((a, b) => b[1].length - a[1].length)
      .slice(0, 6);
  };

  return (
    <div className="message-reactions relative">
      <div className="flex items-center space-x-1">
        {/* Existing Reactions */}
        <AnimatePresence>
          {getTopReactions().map(([reactionName, users]) => {
            const isActive = hasUserReacted(reactionName);
            
            return (
              <motion.button
                key={reactionName}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => handleReaction(reactionName)}
                disabled={isUpdating}
                className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                  isActive 
                    ? 'bg-primary/20 text-primary border border-primary/30' 
                    : 'bg-white/10 text-white/80 border border-white/20 hover:bg-white/20'
                }`}
              >
                <span>{reactionName}</span>
                <span>{users.length}</span>
              </motion.button>
            );
          })}
        </AnimatePresence>

        {/* Removed Add Reaction Button - not needed for simple chat */}
      </div>

      {/* Click outside to close */}
      {showReactionPicker && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowReactionPicker(false)}
        />
      )}
    </div>
  );
};

export default MessageReactions;